import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  filterSourcingToolBridgeGroups,
} from '@/pages/ipe/workbench/constants/sourcingToolBridgeConfig';
import {
  buildDetailToolbarBridgeGroups,
  getDetailToolbarPreset,
} from '@/pages/ipe/workbench/constants/detailToolbarPresets';

/**
 * 将「本地商品库」从多功能分组中拆成独立入口，便于一点即开弹窗。
 * 例如素材采集工具栏的「POD刊登」含本地商品库/刊登任务/定时上架时，本地商品库单独成按钮。
 * 同组若还有「SPU 复色」，两者合成一个下拉：本地商品库 / SPU 复色。
 */
function hoistPlatformListingGroup(groups) {
  if (!Array.isArray(groups) || groups.length === 0) return groups;
  const result = [];
  let listingGroup = null;
  for (const group of groups) {
    const items = Array.isArray(group?.items) ? group.items : [];
    const listingItem = items.find((item) => item?.id === 'platformListing');
    if (!listingItem) {
      result.push(group);
      continue;
    }
    const recolorItem = items.find((item) => item?.id === 'platformRecolor');
    const rest = items.filter(
      (item) => item?.id !== 'platformListing' && item?.id !== 'platformRecolor',
    );
    if (!listingGroup) {
      listingGroup = {
        id: recolorItem ? 'listingWithRecolor' : 'listing',
        label: group?.label || '',
        ...(group?.labelKey ? { labelKey: group.labelKey } : {}),
        items: recolorItem ? [listingItem, recolorItem] : [listingItem],
      };
    }
    if (rest.length > 0) {
      result.push({ ...group, items: rest });
    }
  }
  if (!listingGroup) return groups;
  // 插在侵权检测之前，尽量贴近商品设计等页的按钮顺序
  const infringementIdx = result.findIndex(
    (g) => g?.id === 'infringement'
      || (Array.isArray(g?.items) && g.items.some((i) => i?.id === 'infringementDetection')),
  );
  if (infringementIdx >= 0) {
    result.splice(infringementIdx, 0, listingGroup);
  } else {
    result.push(listingGroup);
  }
  return result;
}

function QuickToolBridgeGroupMenu({
  group,
  label,
  disabled,
  busy,
  onSelectNav,
}) {
  const { t } = useTranslation('workbench');
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const handleSelect = (navId) => {
    setOpen(false);
    onSelectNav?.(navId);
  };

  const itemLabel = (item) => (
    (item?.labelKey ? t(item.labelKey, { defaultValue: '' }) : '') || item?.label || ''
  );

  return (
    <div
      ref={rootRef}
      className={`sourcing-tool-bridge__group${open ? ' sourcing-tool-bridge__group--open' : ''}`}
    >
      <button
        type="button"
        className="sourcing-tool-bridge__group-btn"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('detailToolbar.groupToolsAria', { label })}
        onClick={() => setOpen((v) => !v)}
      >
        {busy ? <Loader2 size={14} className="sourcing-tool-bridge__spin" aria-hidden /> : null}
        <span>{label}</span>
      </button>
      {open ? (
        <div className="sourcing-tool-bridge__menu" role="menu" aria-label={t('detailToolbar.groupMenuAria', { label })}>
          {group.items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              className="sourcing-tool-bridge__menu-item"
              onClick={() => handleSelect(item.id)}
            >
              <span className="sourcing-tool-bridge__menu-label">{itemLabel(item)}</span>
              {item.cardDesc ? (
                <span className="sourcing-tool-bridge__menu-desc">{item.cardDesc}</span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * 详情页 / 采集详情共用快捷功能工具栏。
 * 选择功能后的建任务 + Toast 跳转由 {@link useDetailQuickToolBridge} 负责。
 *
 * @param {'gallery'|'sourcing'} [variant='gallery']
 *   gallery：按 currentNavId / excludeNavIds 过滤；sourcing：展示全部分组
 */
export default function DetailQuickToolBridgeToolbar({
  variant = 'gallery',
  selectedCount = 0,
  disabled = false,
  busy = false,
  onSelectNav,
  currentNavId = '',
  excludeNavIds = null,
  /** 为 true 时不渲染工具分组下拉（仅保留导出 / extra 等） */
  hideGroups = false,
  /** 指定分组列表时优先使用（如商品库仅 POD 工具） */
  groups = null,
  onExportByTemplate = null,
  extra = null,
  className = '',
  ariaLabel = '',
}) {
  const { t } = useTranslation('workbench');
  const isDisabled = disabled || busy;
  const toolbarPreset = useMemo(
    () => getDetailToolbarPreset(currentNavId),
    [currentNavId],
  );
  const bridgeGroups = useMemo(() => {
    if (hideGroups) return [];
    const presetGroups = buildDetailToolbarBridgeGroups(currentNavId);
    let next = null;
    if (Array.isArray(presetGroups) && presetGroups.length > 0) {
      next = presetGroups;
    } else if (Array.isArray(groups) && groups.length > 0) {
      next = groups;
    } else {
      const exclude = new Set(
        Array.isArray(excludeNavIds) ? excludeNavIds.filter(Boolean).map(String) : [],
      );
      if (currentNavId) exclude.add(String(currentNavId));
      // 素材采集页本身在白名单；其它详情页按 currentNavId 决定是否展示「商品库」
      if (variant === 'sourcing') {
        next = filterSourcingToolBridgeGroups(exclude, {
          sourceNavId: currentNavId || 'sourcingHub',
          allowPlatformListing: true,
        });
      } else {
        next = filterSourcingToolBridgeGroups(exclude, { sourceNavId: currentNavId });
      }
    }
    return hoistPlatformListingGroup(next);
  }, [variant, currentNavId, excludeNavIds, hideGroups, groups]);

  const showExportByTemplate = typeof onExportByTemplate === 'function'
    && !toolbarPreset?.hideExport;

  const resolveItemLabel = (item) => (
    (item?.labelKey ? t(item.labelKey, { defaultValue: '' }) : '') || item?.label || ''
  );

  const resolveGroupLabel = (group) => {
    const items = Array.isArray(group?.items) ? group.items : [];
    if (items.length === 1) return resolveItemLabel(items[0]) || group?.label || '';
    if (group?.labelKey) {
      const labelKeyLabel = t(group.labelKey, { defaultValue: '' });
      if (labelKeyLabel) return labelKeyLabel;
    }
    if (group?.label) return group.label;
    if (group?.titleKey) {
      const sectionLabel = t(`${group.titleKey}.title`, { defaultValue: '' });
      if (sectionLabel) return sectionLabel;
    }
    const groupKey = group?.id ? `detailToolbar.groups.${group.id}` : '';
    const presetLabel = groupKey ? t(groupKey, { defaultValue: '' }) : '';
    return presetLabel || group?.label || '';
  };

  const rootClass = [
    'sourcing-tool-bridge',
    variant === 'gallery' ? 'ai-image-detail__gallery-bridge' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={rootClass}
      aria-label={ariaLabel || (variant === 'sourcing' ? t('detailToolbar.sourcingAria') : t('detailToolbar.detailAria'))}
      data-selected-count={selectedCount}
    >
      <div className="sourcing-tool-bridge__inner">
        <div
          className={`sourcing-tool-bridge__groups${variant === 'gallery' ? ' ai-image-detail__gallery-toolbar' : ''}`}
          role="toolbar"
          aria-label={variant === 'sourcing' ? t('detailToolbar.groupsAria') : undefined}
        >
          {showExportByTemplate ? (
            <button
              type="button"
              className="sourcing-tool-bridge__group-btn"
              disabled={isDisabled}
              onClick={onExportByTemplate}
            >
              {t('studioChrome.exportByTemplate')}
            </button>
          ) : null}
          {bridgeGroups.map((group) => {
            const single = Array.isArray(group.items) && group.items.length === 1
              ? group.items[0]
              : null;
            if (single?.id) {
              return (
                <button
                  key={group.id || single.id}
                  type="button"
                  className="sourcing-tool-bridge__group-btn"
                  disabled={isDisabled}
                  aria-label={resolveItemLabel(single) || resolveGroupLabel(group)}
                  onClick={() => onSelectNav?.(single.id)}
                >
                  {busy ? <Loader2 size={14} className="sourcing-tool-bridge__spin" aria-hidden /> : null}
                  <span>{resolveItemLabel(single) || resolveGroupLabel(group)}</span>
                </button>
              );
            }
            return (
              <QuickToolBridgeGroupMenu
                key={group.id}
                group={group}
                label={resolveGroupLabel(group)}
                disabled={isDisabled}
                busy={busy}
                onSelectNav={onSelectNav}
              />
            );
          })}
          {extra}
        </div>
      </div>
    </div>
  );
}
